import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useSettings } from '../../contexts/SettingsContext';
import type { ContactInformation } from '../../types/settings';

const contactSchema = z.object({
  type: z.string().min(1, 'Type is required'),
  name: z.string().min(1, 'Name is required'),
  value: z.string().min(1, 'Value is required'),
  is_primary: z.boolean().default(false),
  is_public: z.boolean().default(true)
});

type ContactFormData = z.infer<typeof contactSchema>;

export default function ContactInformationForm() {
  const { state, updateContactInformation, addContactInformation, deleteContactInformation } = useSettings();
  const [editingContact, setEditingContact] = useState<ContactInformation | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      type: '',
      name: '',
      value: '',
      is_primary: false,
      is_public: true
    }
  });

  const handleEdit = (contact: ContactInformation) => {
    setEditingContact(contact);
    reset({
      type: contact.type,
      name: contact.name,
      value: contact.value,
      is_primary: contact.is_primary,
      is_public: contact.is_public
    });
    setIsAdding(false);
  };

  const handleAdd = () => {
    setEditingContact(null);
    reset({
      type: '',
      name: '',
      value: '',
      is_primary: false,
      is_public: true
    });
    setIsAdding(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this contact information?')) {
      try {
        await deleteContactInformation(id);
      } catch (error) {
        console.error('Error deleting contact information:', error);
      }
    }
  };

  const onSubmit = async (data: ContactFormData) => {
    try {
      if (editingContact) {
        await updateContactInformation({
          ...editingContact,
          type: data.type,
          name: data.name,
          value: data.value,
          is_primary: data.is_primary,
          is_public: data.is_public
        });
      } else {
        await addContactInformation({
          type: data.type,
          name: data.name,
          value: data.value,
          is_primary: data.is_primary,
          is_public: data.is_public
        });
      }
      
      setEditingContact(null);
      setIsAdding(false);
      reset();
    } catch (error) {
      console.error('Error saving contact information:', error);
    }
  };

  const cancelEdit = () => {
    setEditingContact(null);
    setIsAdding(false);
    reset();
  };

  // Group contacts by type
  const groupedContacts = state.contactInformation.reduce((acc, contact) => {
    if (!acc[contact.type]) {
      acc[contact.type] = [];
    }
    acc[contact.type].push(contact);
    return acc;
  }, {} as Record<string, ContactInformation[]>);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-golf-gray">Contact Information</h2>
        <button
          onClick={handleAdd}
          className="flex items-center space-x-2 px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-orange/90 transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          <span>Add Contact</span>
        </button>
      </div>

      {(isAdding || editingContact) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-golf-gray mb-4">
            {editingContact ? 'Edit Contact Information' : 'Add New Contact Information'}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  {...register('type')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                >
                  <option value="">Select a type</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="address">Address</option>
                  <option value="social">Social Media</option>
                  <option value="other">Other</option>
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                  placeholder="e.g., General Inquiries, Main Office"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('value')}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                placeholder="e.g., info@ibomopen.com, +234 803 123 4567"
              />
              {errors.value && (
                <p className="mt-1 text-sm text-red-600">{errors.value.message}</p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_primary"
                  {...register('is_primary')}
                  className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                />
                <label htmlFor="is_primary" className="ml-2 block text-sm text-gray-700">
                  Primary contact for this type
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_public"
                  {...register('is_public')}
                  className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
                />
                <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
                  Publicly visible
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
              >
                {editingContact ? 'Update Contact' : 'Add Contact'}
              </button>
            </div>
          </form>
        </div>
      )}

      {state.loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
        </div>
      ) : state.error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {state.error}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedContacts).map(([type, contacts]) => (
            <div key={type} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-medium text-golf-gray capitalize">{type}</h3>
              </div>
              <div className="divide-y divide-gray-200">
                {contacts.map((contact) => (
                  <div key={contact.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <h4 className="text-md font-medium text-golf-gray">{contact.name}</h4>
                          {contact.is_primary && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Primary
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{contact.value}</p>
                        {contact.is_public && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-2">
                            Public
                          </span>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(contact)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(contact.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {Object.keys(groupedContacts).length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <p className="text-gray-500">No contact information found. Add your first contact using the button above.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}